// src/services/collaborationService.js

import {
  collection,
  collectionGroup,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  onSnapshot,
  deleteField
} from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { getAuth } from 'firebase/auth';

class CollaborationService {
  constructor() {
    this.auth = getAuth();
  }

  /**
   * The collaborator doc for a given project+user. Deterministic (keyed by userId, not a
   * random auto-ID) so there can only ever be one collaborator relationship per user per
   * project - whichever party (owner invite or self-request) writes first creates it,
   * closing the race where both could otherwise create separate docs for the same person.
   * @param {string} projectId
   * @param {string} userId
   */
  _collaboratorDocRef(projectId, userId) {
    return doc(db, 'projects', projectId, 'collaborators', userId);
  }

  /**
   * Check if user has permission to edit a project
   * @param {string} projectId - The project ID
   * @param {string} userId - The user ID (optional, uses current user if not provided)
   * @returns {Promise<{canEdit: boolean, isOwner: boolean, isCollaborator: boolean}>}
   */
  async checkProjectPermissions(projectId, userId = null) {
    try {
      const currentUser = this.auth.currentUser;
      if (!currentUser) {
        return { canEdit: false, isOwner: false, isCollaborator: false, isCompanyOwner: false };
      }

      const targetUserId = userId || currentUser.uid;

      // Get project data - single read for all permission checking
      const projectRef = doc(db, 'projects', projectId);
      const projectSnap = await getDoc(projectRef);

      if (!projectSnap.exists()) {
        return { canEdit: false, isOwner: false, isCollaborator: false, isCompanyOwner: false };
      }

      const projectData = projectSnap.data();

      // Check if user is the project owner
      if (projectData.createdBy === targetUserId) {
        return { canEdit: true, isOwner: true, isCollaborator: true, isCompanyOwner: false };
      }

      // Check collaborators object in project document
      const collaborators = projectData.collaborators || {};
      const collaborator = collaborators[targetUserId];

      if (collaborator && collaborator.status === 'accepted') {
        return {
          canEdit: collaborator.role === 'pm_collaborator',
          isOwner: false,
          isCollaborator: true,
          isCompanyOwner: false
        };
      }

      // Fallback: Check user profile arrays for backward compatibility
      const userRef = doc(db, 'users', targetUserId);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        const userData = userSnap.data();
        const managedProjects = userData.managedProjects || [];
        const viewingProjects = userData.viewedProjects || userData.viewingProjects || [];

        const isManagedProject = managedProjects.includes(projectId);
        const isViewingProject = viewingProjects.includes(projectId);
        const isCollaborator = isManagedProject || isViewingProject;

        if (isCollaborator) {
          return {
            canEdit: isManagedProject,
            isOwner: false,
            isCollaborator: true,
            isCompanyOwner: false
          };
        }
      }

      // Not an owner/collaborator by any of the above - check if they're a company account
      // owner viewing a project elsewhere in their company (view-only; matches the
      // isCompanyOwnerOfProject Firestore rule that already grants them read access).
      if (projectData.companyId) {
        const isCompanyOwner = await this.isAccountOwnerOfCompany(targetUserId, projectData.companyId);
        if (isCompanyOwner) {
          return { canEdit: false, isOwner: false, isCollaborator: false, isCompanyOwner: true };
        }
      }

      return { canEdit: false, isOwner: false, isCollaborator: false, isCompanyOwner: false };
    } catch (error) {
      console.error('Error checking project permissions:', error);
      return { canEdit: false, isOwner: false, isCollaborator: false, isCompanyOwner: false };
    }
  }

  /**
   * Check whether a user is an account owner of a given company
   * @param {string} userId
   * @param {string} companyId
   * @returns {Promise<boolean>}
   */
  async isAccountOwnerOfCompany(userId, companyId) {
    try {
      const userSnap = await getDoc(doc(db, 'users', userId));
      if (!userSnap.exists() || userSnap.data().companyId !== companyId) {
        return false;
      }

      const companySnap = await getDoc(doc(db, 'companies', companyId));
      if (!companySnap.exists()) return false;

      const companyData = companySnap.data();
      const ownerIds = Array.isArray(companyData.accountOwnerIds)
        ? companyData.accountOwnerIds
        : (companyData.accountOwnerId ? [companyData.accountOwnerId] : []);
      return ownerIds.includes(userId);
    } catch (error) {
      console.error('Error checking account owner status:', error);
      return false;
    }
  }

  /**
   * Get available PM colleagues for collaboration
   * @param {string} projectId - The project ID
   * @returns {Promise<Array>}
   */
  async getAvailablePMColleagues(projectId) {
    try {
      const currentUser = this.auth.currentUser;
      if (!currentUser) {
        return [];
      }

      // Get current user's company
      const userDocRef = doc(db, 'users', currentUser.uid);
      const userDocSnap = await getDoc(userDocRef);
      
      if (!userDocSnap.exists()) {
        return [];
      }

      const userData = userDocSnap.data();
      const companyId = userData.companyId;

      if (!companyId) {
        return [];
      }

      // Get all PMs in the same company
      const usersQuery = query(
        collection(db, 'users'), 
        where('companyId', '==', companyId),
        where('role', '==', 'pm')
      );
      const usersSnap = await getDocs(usersQuery);
      
      const allPMs = usersSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Get current collaborators
      const collaborators = await this.getProjectCollaborators(projectId);
      const collaboratorIds = collaborators.map(collab => collab.userId);

      // Get project owner ID
      const projectRef = doc(db, 'projects', projectId);
      const projectSnap = await getDoc(projectRef);
      const projectData = projectSnap.data();
      const projectOwnerId = projectData.createdBy;

      // Filter out current collaborators and project owner
      const availablePMs = allPMs.filter(pm => 
        pm.id !== currentUser.uid && 
        pm.id !== projectOwnerId && 
        !collaboratorIds.includes(pm.id)
      );

      return availablePMs;
    } catch (error) {
      console.error('Error getting available PM colleagues:', error);
      return [];
    }
  }

  /**
   * Validate if an email belongs to a PM in the same company
   * @param {string} email - The email to validate
   * @param {string} projectId - The project ID
   * @returns {Promise<{isValid: boolean, pmUser?: Object, message?: string}>}
   */
  async validatePMByEmail(email, projectId) {
    try {
      const currentUser = this.auth.currentUser;
      if (!currentUser) {
        return { isValid: false, message: 'You must be logged in to validate emails' };
      }

      // Get current user's company
      const userDocRef = doc(db, 'users', currentUser.uid);
      const userDocSnap = await getDoc(userDocRef);
      
      if (!userDocSnap.exists()) {
        return { isValid: false, message: 'User not found' };
      }

      const userData = userDocSnap.data();
      const companyId = userData.companyId;

      if (!companyId) {
        return { isValid: false, message: 'No company associated with your account' };
      }

      // Find PM by email in the same company
      const usersQuery = query(
        collection(db, 'users'), 
        where('email', '==', email),
        where('companyId', '==', companyId),
        where('role', '==', 'pm')
      );
      const usersSnap = await getDocs(usersQuery);
      
      if (usersSnap.empty) {
        return { isValid: false, message: 'No Project Manager found with this email in your company' };
      }

      const pmUser = { id: usersSnap.docs[0].id, ...usersSnap.docs[0].data() };

      // Check if PM is already a collaborator (accepted, or pending from an owner invite -
      // declined can be re-invited). A pending SELF-request is deliberately let through here:
      // invitePMCollaboratorByEmail() detects that case itself and approves it instead of
      // creating a duplicate invite.
      const collaborators = await this.getProjectCollaborators(projectId);
      const isAlreadyCollaborator = collaborators.some(collab =>
        collab.userId === pmUser.id &&
        (collab.status === 'accepted' || (collab.status === 'pending' && collab.invitedBy !== pmUser.id))
      );

      if (isAlreadyCollaborator) {
        return { isValid: false, message: 'This Project Manager is already a team member' };
      }

      // Check if PM is the project owner
      const projectRef = doc(db, 'projects', projectId);
      const projectSnap = await getDoc(projectRef);
      const projectData = projectSnap.data();
      
      if (pmUser.id === projectData.createdBy) {
        return { isValid: false, message: 'This email belongs to the project owner' };
      }

      return { isValid: true, pmUser };
    } catch (error) {
      console.error('Error validating PM email:', error);
      return { isValid: false, message: 'Failed to validate email. Please try again.' };
    }
  }

  /**
   * Invite a PM colleague to collaborate on a project
   * @param {string} projectId - The project ID
   * @param {string} pmUserId - The PM user ID to invite
   * @param {string} role - The role for the collaborator (optional)
   * @returns {Promise<{success: boolean, message: string}>}
   */
  async invitePMCollaborator(projectId, pmUserId, role = 'pm_collaborator') {
    try {
      const currentUser = this.auth.currentUser;
      if (!currentUser) {
        return { success: false, message: 'You must be logged in to invite collaborators' };
      }

      // Check if current user is the project owner
      const permissions = await this.checkProjectPermissions(projectId);
      if (!permissions.isOwner) {
        return { success: false, message: 'Only project owners can invite collaborators' };
      }

      // Get PM user data
      const pmUserRef = doc(db, 'users', pmUserId);
      const pmUserSnap = await getDoc(pmUserRef);
      
      if (!pmUserSnap.exists()) {
        return { success: false, message: 'PM user not found' };
      }

      const pmUserData = pmUserSnap.data();

      // Verify the user is a PM
      if (!pmUserData.role || pmUserData.role !== 'pm') {
        return { success: false, message: 'Only Project Managers can be invited as collaborators' };
      }

      // Collaborator docs are keyed by userId (deterministic - see _collaboratorDocRef),
      // so there's at most one relationship per user per project, ever. Read that single
      // doc rather than a query.
      const collabRef = this._collaboratorDocRef(projectId, pmUserId);
      const existingSnap = await getDoc(collabRef);

      if (existingSnap.exists()) {
        const existingData = existingSnap.data();
        if (existingData.status === 'accepted') {
          return { success: false, message: 'This PM is already a collaborator on this project' };
        }
        if (existingData.status === 'pending') {
          if (existingData.invitedBy === pmUserId) {
            // They already asked to join - inviting them now is the same thing as
            // approving their request. Do that instead of erroring or double-writing.
            return this.approveCollaboratorRequest(projectId, pmUserId);
          }
          return { success: false, message: 'This PM is already a collaborator on this project' };
        }
        // Declined: fall through and overwrite with a fresh invite (same doc ID).
      }

      // Check if user is the project owner
      const projectRef = doc(db, 'projects', projectId);
      const projectSnap = await getDoc(projectRef);
      const projectData = projectSnap.data();

      if (projectData.createdBy === pmUserId) {
        return { success: false, message: 'Cannot invite the project owner as a collaborator' };
      }

      // Create (or overwrite a declined) collaboration invite in subcollection
      await setDoc(collabRef, {
        userId: pmUserId,
        userEmail: pmUserData.email,
        userName: pmUserData.displayName || pmUserData.email,
        invitedBy: currentUser.uid,
        invitedByName: currentUser.displayName || currentUser.email,
        role: role,
        status: 'pending', // pending, accepted, declined
        invitedAt: serverTimestamp(),
        respondedAt: null
      });

      // Also add to project document for fast permission checking
      await updateDoc(projectRef, {
        [`collaborators.${pmUserId}`]: {
          role: role,
          status: 'pending',
          invitedBy: currentUser.uid,
          invitedByName: currentUser.displayName || currentUser.email,
          invitedAt: serverTimestamp(),
          respondedAt: null
        }
      });

      return { success: true, message: 'PM collaboration invite sent successfully' };
    } catch (error) {
      console.error('Error inviting PM collaborator:', error);
      return { success: false, message: 'Failed to send invitation. Please try again.' };
    }
  }

  /**
   * Invite a PM colleague to collaborate on a project by email
   * @param {string} projectId - The project ID
   * @param {string} email - The PM email to invite
   * @param {string} role - The role for the collaborator (optional)
   * @returns {Promise<{success: boolean, message: string}>}
   */
  async invitePMCollaboratorByEmail(projectId, email, role = 'pm_collaborator') {
    try {
      const currentUser = this.auth.currentUser;
      if (!currentUser) {
        return { success: false, message: 'You must be logged in to invite collaborators' };
      }

      // Check if current user is the project owner
      const permissions = await this.checkProjectPermissions(projectId);
      if (!permissions.isOwner) {
        return { success: false, message: 'Only project owners can invite collaborators' };
      }

      // Validate the email belongs to a PM
      const validation = await this.validatePMByEmail(email, projectId);
      if (!validation.isValid) {
        return { success: false, message: validation.message };
      }

      const pmUserData = validation.pmUser;

      // Collaborator docs are keyed by userId (deterministic - see _collaboratorDocRef).
      let roleToUse = role;
      const collabRef = this._collaboratorDocRef(projectId, pmUserData.id);
      const existingSnap = await getDoc(collabRef);
      if (existingSnap.exists()) {
        const existingData = existingSnap.data();
        if (existingData.status === 'pending' && existingData.invitedBy === pmUserData.id) {
          // They already asked to join - inviting them now is the same thing as
          // approving their request.
          return this.approveCollaboratorRequest(projectId, pmUserData.id);
        }
        if (existingData.status === 'declined') {
          roleToUse = existingData.role || role;
        }
      }

      // Create (or overwrite a declined) collaboration invite in subcollection
      await setDoc(collabRef, {
        userId: pmUserData.id,
        userEmail: pmUserData.email,
        userName: pmUserData.displayName || pmUserData.email,
        invitedBy: currentUser.uid,
        invitedByName: currentUser.displayName || currentUser.email,
        role: roleToUse,
        status: 'pending', // pending, accepted, declined
        invitedAt: serverTimestamp(),
        respondedAt: null
      });

      // Also add to project document for fast permission checking
      const projectRef = doc(db, 'projects', projectId);
      await updateDoc(projectRef, {
        [`collaborators.${pmUserData.id}`]: {
          role: roleToUse,
          status: 'pending',
          invitedBy: currentUser.uid,
          invitedByName: currentUser.displayName || currentUser.email,
          invitedAt: serverTimestamp(),
          respondedAt: null
        }
      });

      return { 
        success: true, 
        message: `Invitation sent to ${pmUserData.displayName || pmUserData.email}` 
      };
    } catch (error) {
      console.error('Error inviting PM collaborator by email:', error);
      return { success: false, message: 'Failed to send invitation. Please try again.' };
    }
  }

  /**
   * Re-invite a previously declined collaborator (reuses their last role)
   * Firestore rules: project owner can delete collaborators docs and create new ones
   * @param {string} projectId - The project ID
   * @param {object} member - { id, userId, userEmail, userName, role }
   * @returns {Promise<{success: boolean, message: string}>}
   */
  async reinviteCollaborator(projectId, member) {
    try {
      const currentUser = this.auth.currentUser;
      if (!currentUser) {
        return { success: false, message: 'You must be logged in to re-invite' };
      }

      const permissions = await this.checkProjectPermissions(projectId);
      if (!permissions.isOwner) {
        return { success: false, message: 'Only project owners can re-invite collaborators' };
      }

      const oldRef = doc(db, 'projects', projectId, 'collaborators', member.id);
      const collabSnap = await getDoc(oldRef);
      if (!collabSnap.exists()) {
        return { success: false, message: 'Collaborator record not found' };
      }
      const data = collabSnap.data();
      if (data.status !== 'declined') {
        return { success: false, message: 'Can only re-invite users who declined' };
      }

      const role = member.role || data.role || 'pm_collaborator';
      const newRef = this._collaboratorDocRef(projectId, member.userId);

      // Docs from before the deterministic-ID change may still sit under a random doc ID -
      // clean that up when this reinvite moves them onto the userId-keyed doc.
      if (oldRef.id !== newRef.id) {
        await deleteDoc(oldRef);
      }

      await setDoc(newRef, {
        userId: member.userId,
        userEmail: member.userEmail || data.userEmail,
        userName: member.userName || data.userName,
        invitedBy: currentUser.uid,
        invitedByName: currentUser.displayName || currentUser.email,
        role,
        status: 'pending',
        invitedAt: serverTimestamp(),
        respondedAt: null
      });

      const projectRef = doc(db, 'projects', projectId);
      await updateDoc(projectRef, {
        [`collaborators.${member.userId}`]: {
          role,
          status: 'pending',
          invitedBy: currentUser.uid,
          invitedByName: currentUser.displayName || currentUser.email,
          invitedAt: serverTimestamp(),
          respondedAt: null
        }
      });

      return { success: true, message: `Re-invitation sent to ${member.userName || member.userEmail}` };
    } catch (error) {
      console.error('Error re-inviting collaborator:', error);
      return { success: false, message: 'Failed to re-invite. Please try again.' };
    }
  }

  /**
   * Accept a collaboration invite
   * @param {string} projectId - The project ID
   * @param {string} inviteId - The invite ID
   * @returns {Promise<{success: boolean, message: string}>}
   */
  async acceptInvite(projectId, inviteId) {
    try {
      const currentUser = this.auth.currentUser;
      if (!currentUser) {
        return { success: false, message: 'You must be logged in to accept invites' };
      }

      const inviteRef = doc(db, 'projects', projectId, 'collaborators', inviteId);
      const inviteSnap = await getDoc(inviteRef);
      
      if (!inviteSnap.exists()) {
        return { success: false, message: 'Invite not found' };
      }

      const inviteData = inviteSnap.data();
      
      if (inviteData.userId !== currentUser.uid) {
        return { success: false, message: 'This invite is not for you' };
      }

      // Self-requests (invitedBy === userId) can only be resolved by the project owner -
      // rules enforce this too, but fail fast here rather than surfacing a raw permission error.
      if (inviteData.invitedBy === inviteData.userId) {
        return { success: false, message: 'This is your own access request - the project owner needs to approve it.' };
      }

      if (inviteData.status !== 'pending') {
        return { success: false, message: 'This invite has already been responded to' };
      }

      // Update invite status in subcollection
      await updateDoc(inviteRef, {
        status: 'accepted',
        respondedAt: serverTimestamp()
      });

      // Update project document collaborators object
      const projectRef = doc(db, 'projects', projectId);
      await updateDoc(projectRef, {
        [`collaborators.${currentUser.uid}.status`]: 'accepted',
        [`collaborators.${currentUser.uid}.respondedAt`]: serverTimestamp()
      });

      // Update user profile with project reference (for backward compatibility)
      await this.updateUserProjectArrays(projectId, inviteData.role, 'add');

      return { success: true, message: 'Collaboration invite accepted' };
    } catch (error) {
      console.error('Error accepting invite:', error);
      return { success: false, message: 'Failed to accept invitation. Please try again.' };
    }
  }

  /**
   * Decline a collaboration invite
   * @param {string} projectId - The project ID
   * @param {string} inviteId - The invite ID
   * @returns {Promise<{success: boolean, message: string}>}
   */
  async declineInvite(projectId, inviteId) {
    try {
      const currentUser = this.auth.currentUser;
      if (!currentUser) {
        return { success: false, message: 'You must be logged in to decline invites' };
      }

      const inviteRef = doc(db, 'projects', projectId, 'collaborators', inviteId);
      const inviteSnap = await getDoc(inviteRef);
      
      if (!inviteSnap.exists()) {
        return { success: false, message: 'Invite not found' };
      }

      const inviteData = inviteSnap.data();
      
      if (inviteData.userId !== currentUser.uid) {
        return { success: false, message: 'This invite is not for you' };
      }

      // Self-requests (invitedBy === userId) can only be resolved by the project owner -
      // rules enforce this too, but fail fast here rather than surfacing a raw permission error.
      if (inviteData.invitedBy === inviteData.userId) {
        return { success: false, message: 'This is your own access request - the project owner needs to approve it.' };
      }

      if (inviteData.status !== 'pending') {
        return { success: false, message: 'This invite has already been responded to' };
      }

      // Update invite status in subcollection
      await updateDoc(inviteRef, {
        status: 'declined',
        respondedAt: serverTimestamp()
      });

      // Update project document collaborators object
      const projectRef = doc(db, 'projects', projectId);
      await updateDoc(projectRef, {
        [`collaborators.${currentUser.uid}.status`]: 'declined',
        [`collaborators.${currentUser.uid}.respondedAt`]: serverTimestamp()
      });

      return { success: true, message: 'Collaboration invite declined' };
    } catch (error) {
      console.error('Error declining invite:', error);
      return { success: false, message: 'Failed to decline invitation. Please try again.' };
    }
  }

  /**
   * Remove a collaborator from a project
   * @param {string} projectId - The project ID
   * @param {string} collaboratorId - The collaborator ID
   * @returns {Promise<{success: boolean, message: string}>}
   */
  async removeCollaborator(projectId, collaboratorId) {
    try {
      const currentUser = this.auth.currentUser;
      if (!currentUser) {
        return { success: false, message: 'You must be logged in to remove collaborators' };
      }

      // Check if current user is the project owner
      const permissions = await this.checkProjectPermissions(projectId);
      if (!permissions.isOwner) {
        return { success: false, message: 'Only project owners can remove collaborators' };
      }

      // Get collaborator data before removing
      const collaboratorRef = doc(db, 'projects', projectId, 'collaborators', collaboratorId);
      const collaboratorSnap = await getDoc(collaboratorRef);
      
      if (collaboratorSnap.exists()) {
        const collaboratorData = collaboratorSnap.data();
        
        // Remove from project document collaborators object
        const projectRef = doc(db, 'projects', projectId);
        await updateDoc(projectRef, {
          [`collaborators.${collaboratorData.userId}`]: deleteField()
        });
        
        // Update user profile arrays (for backward compatibility)
        await this.updateUserProjectArrays(projectId, collaboratorData.role, 'remove', collaboratorData.userId);
      }

      // Remove from subcollection
      await deleteDoc(collaboratorRef);

      return { success: true, message: 'Collaborator removed successfully' };
    } catch (error) {
      console.error('Error removing collaborator:', error);
      return { success: false, message: 'Failed to remove collaborator. Please try again.' };
    }
  }

  /**
   * Request edit access to a project as a company account owner.
   * Account owners can already view any project in their company (Firestore rules), so this
   * is specifically a request to become a full pm_collaborator (edit rights) - the project
   * owner must approve it, same as a regular invite just flipped in initiation direction.
   * @param {string} projectId - The project ID
   * @returns {Promise<{success: boolean, message: string}>}
   */
  async requestProjectAccess(projectId) {
    try {
      const currentUser = this.auth.currentUser;
      if (!currentUser) {
        return { success: false, message: 'You must be logged in to request access' };
      }

      const projectRef = doc(db, 'projects', projectId);
      const projectSnap = await getDoc(projectRef);
      if (!projectSnap.exists()) {
        return { success: false, message: 'Project not found' };
      }
      const projectData = projectSnap.data();

      if (projectData.createdBy === currentUser.uid) {
        return { success: false, message: 'You already own this project' };
      }

      // Collaborator docs are keyed by userId (deterministic - see _collaboratorDocRef), so
      // there's at most one relationship doc for this user on this project - read it directly
      // rather than a query, and block duplicates.
      const collabRef = this._collaboratorDocRef(projectId, currentUser.uid);
      const existingSnap = await getDoc(collabRef);
      if (existingSnap.exists()) {
        const existingData = existingSnap.data();
        if (existingData.status === 'accepted') {
          return { success: false, message: 'You already have access to this project' };
        }
        if (existingData.status === 'pending') {
          if (existingData.invitedBy !== currentUser.uid) {
            // The owner already invited us - requesting again would just clobber their
            // invite. Point back at it instead of creating a competing doc.
            return { success: false, message: 'The project owner has already invited you - check your notifications to accept.' };
          }
          return { success: false, message: 'You already have a pending request for this project' };
        }
        // Declined: fall through and overwrite with a fresh request (same doc ID).
      }

      const role = 'pm_collaborator';

      // Create (or overwrite a declined) self-request in the subcollection.
      // projectOwnerId lets the owner's bell find pending requests across every project they
      // own with a single collectionGroup query, instead of listing every owned project first
      // (doesn't scale - see requestProjectAccess's callers for the full reasoning).
      await setDoc(collabRef, {
        userId: currentUser.uid,
        userEmail: currentUser.email,
        userName: currentUser.displayName || currentUser.email,
        invitedBy: currentUser.uid,
        invitedByName: currentUser.displayName || currentUser.email,
        role: role,
        status: 'pending',
        invitedAt: serverTimestamp(),
        respondedAt: null,
        projectOwnerId: projectData.createdBy
      });

      // Mirror onto the project document for fast permission checking
      await updateDoc(projectRef, {
        [`collaborators.${currentUser.uid}`]: {
          role: role,
          status: 'pending',
          invitedBy: currentUser.uid,
          invitedByName: currentUser.displayName || currentUser.email,
          invitedAt: serverTimestamp(),
          respondedAt: null
        }
      });

      return { success: true, message: 'Access request sent to the project owner' };
    } catch (error) {
      console.error('Error requesting project access:', error);
      return { success: false, message: 'Failed to send access request. Please try again.' };
    }
  }

  /**
   * Approve a self-requested collaborator access request (project owner only)
   * @param {string} projectId - The project ID
   * @param {string} requestId - The collaborator subcollection doc ID
   * @returns {Promise<{success: boolean, message: string}>}
   */
  async approveCollaboratorRequest(projectId, requestId) {
    try {
      const currentUser = this.auth.currentUser;
      if (!currentUser) {
        return { success: false, message: 'You must be logged in to approve requests' };
      }

      const permissions = await this.checkProjectPermissions(projectId);
      if (!permissions.isOwner) {
        return { success: false, message: 'Only the project owner can approve access requests' };
      }

      const requestRef = doc(db, 'projects', projectId, 'collaborators', requestId);
      const requestSnap = await getDoc(requestRef);
      if (!requestSnap.exists()) {
        return { success: false, message: 'Request not found' };
      }

      const requestData = requestSnap.data();
      if (requestData.invitedBy !== requestData.userId) {
        return { success: false, message: 'This is not a self-requested access request' };
      }
      if (requestData.status !== 'pending') {
        return { success: false, message: 'This request has already been responded to' };
      }

      await updateDoc(requestRef, {
        status: 'accepted',
        respondedAt: serverTimestamp()
      });

      const projectRef = doc(db, 'projects', projectId);
      await updateDoc(projectRef, {
        [`collaborators.${requestData.userId}.status`]: 'accepted',
        [`collaborators.${requestData.userId}.respondedAt`]: serverTimestamp()
      });

      // Unlike acceptInvite, we deliberately skip updateUserProjectArrays here - Firestore
      // rules only allow users to write their own managedProjects/viewedProjects, so the
      // owner can't update the requester's arrays. Web doesn't read those arrays (Sidebar.jsx
      // queries by companyId instead), so this is safe for web. Mobile relies on the arrays
      // and isn't part of this feature yet.

      return { success: true, message: 'Access request approved' };
    } catch (error) {
      console.error('Error approving access request:', error);
      return { success: false, message: 'Failed to approve request. Please try again.' };
    }
  }

  /**
   * Update user project arrays (managedProjects and viewedProjects)
   * Uses viewedProjects to align with mobile and Firebase functions
   * @param {string} projectId - The project ID
   * @param {string} role - The user's role (pm_collaborator or pm_viewer)
   * @param {string} action - 'add' or 'remove'
   * @param {string} userId - The user ID (optional, uses current user if not provided)
   * @returns {Promise<void>}
   */
  async updateUserProjectArrays(projectId, role, action, userId = null) {
    try {
      const currentUser = this.auth.currentUser;
      const targetUserId = userId || currentUser.uid;
      
      if (!targetUserId) {
        console.error('No user ID provided for updating project arrays');
        return;
      }

      const userRef = doc(db, 'users', targetUserId);
      const userSnap = await getDoc(userRef);
      
      if (!userSnap.exists()) {
        console.error('User document not found');
        return;
      }

      const userData = userSnap.data();
      const managedProjects = userData.managedProjects || [];
      const viewedProjects = userData.viewedProjects || userData.viewingProjects || [];

      if (action === 'add') {
        if (role === 'pm_collaborator' && !managedProjects.includes(projectId)) {
          managedProjects.push(projectId);
        } else if ((role === 'pm_viewer' || role === 'viewer') && !viewedProjects.includes(projectId)) {
          viewedProjects.push(projectId);
        }
      } else if (action === 'remove') {
        // Remove from both arrays to be safe
        const managedIndex = managedProjects.indexOf(projectId);
        if (managedIndex > -1) {
          managedProjects.splice(managedIndex, 1);
        }
        
        const viewingIndex = viewedProjects.indexOf(projectId);
        if (viewingIndex > -1) {
          viewedProjects.splice(viewingIndex, 1);
        }
      }

      await updateDoc(userRef, {
        managedProjects: managedProjects,
        viewedProjects: viewedProjects,
        lastUpdated: serverTimestamp()
      });

      console.log(`Updated user ${targetUserId} project arrays:`, {
        managedProjects,
        viewedProjects,
        action,
        projectId,
        role
      });
    } catch (error) {
      console.error('Error updating user project arrays:', error);
    }
  }

  /**
   * Get all collaborators for a project
   * @param {string} projectId - The project ID
   * @returns {Promise<Array>}
   */
  async getProjectCollaborators(projectId) {
    try {
      const collaboratorsQuery = query(
        collection(db, 'projects', projectId, 'collaborators'),
        orderBy('invitedAt', 'desc')
      );
      
      const collaboratorsSnap = await getDocs(collaboratorsQuery);
      return collaboratorsSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting project collaborators:', error);
      return [];
    }
  }

  /**
   * Get pending collaboration invites for the current user
   * Uses direct Firestore collectionGroup query (rules allow userId match for invited users)
   * @returns {Promise<Array>}
   */
  async getPendingInvitesForUser() {
    try {
      const currentUser = this.auth.currentUser;
      if (!currentUser) {
        return [];
      }

      const collaboratorsQuery = query(
        collectionGroup(db, 'collaborators'),
        where('userId', '==', currentUser.uid),
        where('status', '==', 'pending')
      );
      const snapshot = await getDocs(collaboratorsQuery);

      if (snapshot.empty) {
        return [];
      }

      const projectIds = new Set();
      const inviteDocs = [];
      snapshot.docs.forEach(docSnap => {
        const data = docSnap.data();
        // Self-requests (invitedBy === userId, from requestProjectAccess) aren't invites
        // waiting on this user - they're this user's own outbound request waiting on the
        // project owner. Showing them here would let a company account owner see (and
        // action) their own request as if someone else had sent it to them.
        if (data.invitedBy === data.userId) return;
        const pathParts = docSnap.ref.path.split('/');
        if (pathParts[0] === 'projects' && pathParts[1]) {
          projectIds.add(pathParts[1]);
          inviteDocs.push({ doc: docSnap, projectId: pathParts[1] });
        }
      });

      const projectMap = new Map();
      await Promise.all(
        Array.from(projectIds).map(async projectId => {
          try {
            const projectSnap = await getDoc(doc(db, 'projects', projectId));
            if (projectSnap.exists()) {
              projectMap.set(projectId, projectSnap.data()?.name || 'Unnamed Project');
            }
          } catch {
            projectMap.set(projectId, 'Unknown Project');
          }
        })
      );

      return inviteDocs.map(({ doc: docSnap, projectId }) => {
        const data = docSnap.data();
        const invitedAt = data.invitedAt;
        return {
          id: docSnap.id,
          projectId,
          projectName: projectMap.get(projectId) || 'Unknown Project',
          invitedByName: data.invitedByName || 'Unknown',
          role: data.role || 'pm_collaborator',
          invitedAt: invitedAt?.toDate ? invitedAt.toDate() : (invitedAt ? new Date(invitedAt) : null),
          kind: 'invite',
        };
      });
    } catch (error) {
      console.error('Error getting pending invites:', error);
      return [];
    }
  }

  /**
   * Get pending self-requests awaiting approval, across every project the current user owns.
   * Uses projectOwnerId (set at request time) so this is a single indexed query regardless of
   * how many projects the user owns - see requestProjectAccess for the full reasoning.
   * @returns {Promise<Array>}
   */
  async getPendingRequestsForOwner() {
    try {
      const currentUser = this.auth.currentUser;
      if (!currentUser) {
        return [];
      }

      const requestsQuery = query(
        collectionGroup(db, 'collaborators'),
        where('projectOwnerId', '==', currentUser.uid),
        where('status', '==', 'pending')
      );
      const snapshot = await getDocs(requestsQuery);
      if (snapshot.empty) {
        return [];
      }

      const projectIds = new Set();
      const requestDocs = [];
      snapshot.docs.forEach(docSnap => {
        const pathParts = docSnap.ref.path.split('/');
        if (pathParts[0] === 'projects' && pathParts[1]) {
          projectIds.add(pathParts[1]);
          requestDocs.push({ doc: docSnap, projectId: pathParts[1] });
        }
      });

      const projectMap = new Map();
      await Promise.all(
        Array.from(projectIds).map(async projectId => {
          try {
            const projectSnap = await getDoc(doc(db, 'projects', projectId));
            if (projectSnap.exists()) {
              projectMap.set(projectId, projectSnap.data()?.name || 'Unnamed Project');
            }
          } catch {
            projectMap.set(projectId, 'Unknown Project');
          }
        })
      );

      return requestDocs.map(({ doc: docSnap, projectId }) => {
        const data = docSnap.data();
        const invitedAt = data.invitedAt;
        return {
          id: docSnap.id,
          projectId,
          projectName: projectMap.get(projectId) || 'Unknown Project',
          requesterName: data.userName || data.userEmail || 'Unknown',
          role: data.role || 'pm_collaborator',
          invitedAt: invitedAt?.toDate ? invitedAt.toDate() : (invitedAt ? new Date(invitedAt) : null),
          kind: 'request',
        };
      });
    } catch (error) {
      console.error('Error getting pending requests for owner:', error);
      return [];
    }
  }

  /**
   * Listen to collaboration changes for a project
   * @param {string} projectId - The project ID
   * @param {Function} callback - Callback function to handle changes
   * @returns {Function} Unsubscribe function
   */
  listenToCollaborators(projectId, callback) {
    const collaboratorsQuery = query(
      collection(db, 'projects', projectId, 'collaborators'),
      orderBy('invitedAt', 'desc')
    );

    return onSnapshot(collaboratorsQuery, (snapshot) => {
      const collaborators = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      callback(collaborators);
    });
  }

  /**
   * Get user's managed and viewing projects from their profile
   * Uses viewedProjects (aligns with mobile and Firebase functions)
   * @param {string} userId - The user ID (optional, uses current user if not provided)
   * @returns {Promise<{managedProjects: Array, viewedProjects: Array}>}
   */
  async getUserProjects(userId = null) {
    try {
      const currentUser = this.auth.currentUser;
      const targetUserId = userId || currentUser?.uid;
      
      if (!targetUserId) {
        return { managedProjects: [], viewedProjects: [] };
      }

      const userRef = doc(db, 'users', targetUserId);
      const userSnap = await getDoc(userRef);
      
      if (!userSnap.exists()) {
        return { managedProjects: [], viewedProjects: [] };
      }

      const userData = userSnap.data();
      return {
        managedProjects: userData.managedProjects || [],
        viewedProjects: userData.viewedProjects || userData.viewingProjects || []
      };
    } catch (error) {
      console.error('Error getting user projects:', error);
      return { managedProjects: [], viewedProjects: [] };
    }
  }

  /**
   * Listen to pending invites for the current user across all projects
   * Uses Firestore onSnapshot - real-time updates when invites change, no polling
   * @param {Function} callback - Callback function to handle changes
   * @returns {Function} Unsubscribe function
   */
  listenToUserInvites(callback) {
    const currentUser = this.auth.currentUser;
    if (!currentUser) {
      return () => {};
    }

    const collaboratorsQuery = query(
      collectionGroup(db, 'collaborators'),
      where('userId', '==', currentUser.uid),
      where('status', '==', 'pending')
    );

    const unsubscribe = onSnapshot(
      collaboratorsQuery,
      async (snapshot) => {
        if (snapshot.empty) {
          callback([]);
          return;
        }

        const projectIds = new Set();
        const inviteDocs = [];
        snapshot.docs.forEach(docSnap => {
          const data = docSnap.data();
          // Self-requests aren't invites waiting on this user - see getPendingInvitesForUser.
          if (data.invitedBy === data.userId) return;
          const pathParts = docSnap.ref.path.split('/');
          if (pathParts[0] === 'projects' && pathParts[1]) {
            projectIds.add(pathParts[1]);
            inviteDocs.push({ doc: docSnap, projectId: pathParts[1] });
          }
        });

        const projectMap = new Map();
        await Promise.all(
          Array.from(projectIds).map(async projectId => {
            try {
              const projectSnap = await getDoc(doc(db, 'projects', projectId));
              if (projectSnap.exists()) {
                projectMap.set(projectId, projectSnap.data()?.name || 'Unnamed Project');
              }
            } catch {
              projectMap.set(projectId, 'Unknown Project');
            }
          })
        );

        const invites = inviteDocs.map(({ doc: docSnap, projectId }) => {
          const data = docSnap.data();
          const invitedAt = data.invitedAt;
          return {
            id: docSnap.id,
            projectId,
            projectName: projectMap.get(projectId) || 'Unknown Project',
            invitedByName: data.invitedByName || 'Unknown',
            role: data.role || 'pm_collaborator',
            invitedAt: invitedAt?.toDate ? invitedAt.toDate() : (invitedAt ? new Date(invitedAt) : null),
            kind: 'invite',
          };
        });
        callback(invites);
      },
      (error) => {
        console.error('Error listening to invites:', error);
        callback([]);
      }
    );

    return unsubscribe;
  }

  /**
   * Listen to pending self-requests awaiting approval, across every project the current user
   * owns. Mirrors listenToUserInvites - see getPendingRequestsForOwner for the query reasoning.
   * @param {Function} callback - Callback function to handle changes
   * @returns {Function} Unsubscribe function
   */
  listenToOwnerRequests(callback) {
    const currentUser = this.auth.currentUser;
    if (!currentUser) {
      return () => {};
    }

    const requestsQuery = query(
      collectionGroup(db, 'collaborators'),
      where('projectOwnerId', '==', currentUser.uid),
      where('status', '==', 'pending')
    );

    const unsubscribe = onSnapshot(
      requestsQuery,
      async (snapshot) => {
        if (snapshot.empty) {
          callback([]);
          return;
        }

        const projectIds = new Set();
        const requestDocs = [];
        snapshot.docs.forEach(docSnap => {
          const pathParts = docSnap.ref.path.split('/');
          if (pathParts[0] === 'projects' && pathParts[1]) {
            projectIds.add(pathParts[1]);
            requestDocs.push({ doc: docSnap, projectId: pathParts[1] });
          }
        });

        const projectMap = new Map();
        await Promise.all(
          Array.from(projectIds).map(async projectId => {
            try {
              const projectSnap = await getDoc(doc(db, 'projects', projectId));
              if (projectSnap.exists()) {
                projectMap.set(projectId, projectSnap.data()?.name || 'Unnamed Project');
              }
            } catch {
              projectMap.set(projectId, 'Unknown Project');
            }
          })
        );

        const requests = requestDocs.map(({ doc: docSnap, projectId }) => {
          const data = docSnap.data();
          const invitedAt = data.invitedAt;
          return {
            id: docSnap.id,
            projectId,
            projectName: projectMap.get(projectId) || 'Unknown Project',
            requesterName: data.userName || data.userEmail || 'Unknown',
            role: data.role || 'pm_collaborator',
            invitedAt: invitedAt?.toDate ? invitedAt.toDate() : (invitedAt ? new Date(invitedAt) : null),
            kind: 'request',
          };
        });
        callback(requests);
      },
      (error) => {
        console.error('Error listening to owner requests:', error);
        callback([]);
      }
    );

    return unsubscribe;
  }
}

const collaborationService = new CollaborationService();
export default collaborationService;
